import React, { useEffect, useState } from "react";
import { PageLoader, TabNavigation, Button } from "../../ui";
import { FormModal, FormModalHeader, FormModalBody, FormModalField, FormModalFooter, formInputCls } from "../../ui/FormModal";
import VaptSeverityBadge from "../components/VaptSeverityBadge";
import VaptStatusBadge from "../components/VaptStatusBadge";
import { DataTable } from "../../common/DataTable";
import { useAuth } from "../../../context/AuthContext";
import {
  listVaptRiskApi, listVaptRiskAcceptanceApi, getVaptRiskScoreApi,
  createVaptRiskAcceptanceApi, decideVaptRiskAcceptanceApi,
} from "../../../services/axios/AuthApis/VAPTApis";
import { VAPT_SEVERITIES } from "../utils/vaptConstants";

const asArray = (data) => (Array.isArray(data) ? data : data?.results || []);
const PLATFORM_ADMIN_ROLES = ["platform_admin", "admin"];

// Risk has no dedicated backend model — this view is derived client-side
// from real findings data (severity + validation_status) rather than a
// separate "risk" endpoint, since the backend does not expose one.
const VaptRisk = () => {
  const { user } = useAuth();
  const isPlatformAdmin = PLATFORM_ADMIN_ROLES.includes(user?.role);
  const [risk, setRisk] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("overview");
  const [acceptance, setAcceptance] = useState([]);
  const [riskScore, setRiskScore] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestForm, setRequestForm] = useState({ finding: "", reason: "" });
  const [savingRequest, setSavingRequest] = useState(false);
  const [notice, setNotice] = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([listVaptRiskApi(), listVaptRiskAcceptanceApi(), getVaptRiskScoreApi()])
      .then(([data, acc, score]) => {
        const rows = asArray(data).map((f) => ({
          ...f,
          // VaptTable needs a unique row `id` and uses the human-readable
          // finding_id for it (never the raw Mongo id as visible text) -
          // but Risk Acceptance's `finding` field is a real ReferenceField
          // that needs the actual Mongo id, so it's kept separately here
          // rather than lost when `id` gets overwritten below.
          mongo_id: f.id,
          id: f.finding_id,
          status: f.validation_status === "Accepted Risk" ? "Accepted" : "Open",
        }));
        setRisk(rows);
        setAcceptance(asArray(acc));
        setRiskScore(score);
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.message || "Failed to load risk data.");
        setLoading(false);
      });
  };

  useEffect(load, []);

  const submitRequest = async (e) => {
    e.preventDefault();
    if (!requestForm.finding || !requestForm.reason.trim()) return;
    setSavingRequest(true);
    try {
      // Findings — created a real VAPTFinding, so its Mongo `id` is on the
      // row already (rest_framework_mongoengine's default `id`).
      await createVaptRiskAcceptanceApi({ finding: requestForm.finding, reason: requestForm.reason.trim() });
      setShowRequestModal(false);
      setRequestForm({ finding: "", reason: "" });
      load();
    } catch (err) {
      setNotice(err?.response?.data?.error || err?.message || "Failed to submit risk acceptance request.");
    } finally {
      setSavingRequest(false);
    }
  };

  const decide = async (acceptanceId, decision) => {
    try {
      await decideVaptRiskAcceptanceApi(acceptanceId, decision);
      load();
    } catch {
      setNotice("Failed to decide on this risk acceptance request.");
    }
  };

  if (loading) return <PageLoader title="Loading risk data…" />;
  if (error) {
    return (
      <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
        <p className="text-gray-500">{error}</p>
      </div>
    );
  }

  const tabs = [
    { id: "overview", name: "Risk Overview" },
    { id: "mapping", name: "Risk Mapping" },
    { id: "acceptance", name: "Risk Acceptance" },
  ];

  const bySeverity = VAPT_SEVERITIES.reduce((acc, s) => {
    acc[s] = risk.filter((r) => r.severity === s).length;
    return acc;
  }, {});
  const maxCount = Math.max(...Object.values(bySeverity), 1);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Risk</h2>
          <p className="text-sm text-gray-500">Consolidated risk view derived from VAPT findings</p>
        </div>
        {tab === "acceptance" && (
          <Button
            size="sm"
            onClick={() => {
              setRequestForm({ finding: risk[0]?.mongo_id || "", reason: "" });
              setShowRequestModal(true);
            }}
            disabled={risk.length === 0}
          >
            Request Risk Acceptance
          </Button>
        )}
      </div>

      {notice && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2.5">{notice}</div>}

      <TabNavigation tabs={tabs} activeTab={tab} onTabChange={setTab} boxedActive />

      {tab === "overview" && (
        <div className="space-y-4">
          {riskScore && (
            <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-center gap-6">
              <div>
                <p className="text-xs text-gray-500">Aggregate Risk Score (0-100)</p>
                <p className="text-3xl font-bold text-gray-900">{riskScore.aggregate_risk_score}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Risk Level</p>
                <VaptSeverityBadge severity={riskScore.aggregate_risk_level} size="sm" />
              </div>
      
            </div>
          )}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-gray-800 mb-4">Risk by Severity</h3>
            <div className="space-y-3">
              {VAPT_SEVERITIES.map((s) => (
                <div key={s} className="flex items-center gap-3">
                  <span className="w-24 text-xs text-gray-600">{s}</span>
                  <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary-500"
                      style={{ width: `${(bySeverity[s] / maxCount) * 100}%`, backgroundColor: "var(--app-primary-color, #26256b)" }}
                    />
                  </div>
                  <span className="w-8 text-xs font-semibold text-gray-700 text-right">{bySeverity[s]}</span>
                </div>
              ))}
            </div>
          </div>
          <DataTable
            columns={[
              {
                id: "title",
                accessorFn: (r) => r.title,
                header: "Risk",
                enableSorting: false,
                meta: { filter: { variant: "text", label: "Risk", placeholder: "Search risk" } },
                cell: ({ row }) => <span>{row.original.title}</span>,
              },
              {
                id: "severity",
                accessorFn: (r) => r.severity,
                header: "Severity",
                enableSorting: false,
                meta: {
                  filter: {
                    variant: "select",
                    label: "Severity",
                    placeholder: "All severities",
                    options: VAPT_SEVERITIES.map((v) => ({ value: v, label: v })),
                  },
                },
                cell: ({ row }) => <VaptSeverityBadge severity={row.original.severity} size="sm" />,
              },
              {
                id: "status",
                accessorFn: (r) => r.status,
                header: "Status",
                enableSorting: false,
                meta: {
                  filter: {
                    variant: "select",
                    label: "Status",
                    placeholder: "All statuses",
                    options: [
                      { value: "Open", label: "Open" },
                      { value: "Accepted", label: "Accepted" },
                    ],
                  },
                },
                cell: ({ row }) => <VaptStatusBadge status={row.original.status} size="sm" />,
              },
              {
                id: "project",
                accessorFn: (r) => r.project_name || r.project_display_id,
                header: "Project",
                enableSorting: false,
                meta: {
                  filter: {
                    variant: "select",
                    label: "Project",
                    placeholder: "All projects",
                    options: [...new Set(risk.map((r) => r.project_name || r.project_display_id).filter(Boolean))].map((v) => ({ value: v, label: v })),
                  },
                },
                cell: ({ row }) => <span>{row.original.project_name || row.original.project_display_id || "Asset VAPT Project"}</span>,
              },
            ]}
            data={risk}
            getRowId={(r) => String(r.id)}
            emptyMessage="No findings recorded yet."
            enableColumnVisibility={false}
            enableColumnResizing
            enableColumnFilters
            maxBodyHeight="65vh"
          />
        </div>
      )}

      {tab === "mapping" && (
        <div className="space-y-2">
          <p className="text-xs text-gray-400">CWE / OWASP / CVSS chain plus business risk context per finding.</p>
          <DataTable
            columns={[
              {
                id: "title",
                accessorFn: (r) => r.title,
                header: "Finding",
                enableSorting: false,
                meta: { filter: { variant: "text", label: "Finding", placeholder: "Search finding" } },
                cell: ({ row }) => <span>{row.original.title}</span>,
              },
              {
                id: "cwe_id",
                accessorFn: (r) => r.cwe_id,
                header: "CWE",
                enableSorting: false,
                meta: { filter: { variant: "text", label: "CWE", placeholder: "Search CWE" } },
                cell: ({ row }) => <span>{row.original.cwe_id ?? "—"}</span>,
              },
              {
                id: "owasp",
                accessorFn: (r) => r.referenceMapping?.owasp,
                header: "OWASP",
                enableSorting: false,
                meta: {
                  filter: {
                    variant: "select",
                    label: "OWASP",
                    placeholder: "All OWASP categories",
                    options: [...new Set(risk.map((r) => r.referenceMapping?.owasp).filter(Boolean))].map((v) => ({ value: v, label: v })),
                  },
                },
                cell: ({ row }) => <span>{row.original.referenceMapping?.owasp ?? "—"}</span>,
              },
              {
                id: "cvss",
                accessorFn: (r) => r.referenceMapping?.cvss,
                header: "CVSS (Technical Severity)",
                enableSorting: false,
                cell: ({ row }) => <span>{row.original.referenceMapping?.cvss ?? "—"}</span>,
              },
              {
                id: "business_risk",
                accessorFn: (r) => r.referenceMapping?.business_risk_context?.business_risk,
                header: "Business Risk",
                enableSorting: false,
                meta: {
                  filter: {
                    variant: "select",
                    label: "Business Risk",
                    placeholder: "All",
                    options: [...new Set(risk.map((r) => r.referenceMapping?.business_risk_context?.business_risk).filter(Boolean))].map((v) => ({ value: v, label: v })),
                  },
                },
                cell: ({ row }) => <span>{row.original.referenceMapping?.business_risk_context?.business_risk ?? "—"}</span>,
              },
              {
                id: "sensitive_data",
                accessorFn: (r) => r.referenceMapping?.business_risk_context?.sensitive_data,
                header: "Sensitive Data",
                enableSorting: false,
                meta: {
                  filter: {
                    variant: "select",
                    label: "Sensitive Data",
                    placeholder: "All",
                    options: [...new Set(risk.map((r) => r.referenceMapping?.business_risk_context?.sensitive_data).filter(Boolean))].map((v) => ({ value: v, label: v })),
                  },
                },
                cell: ({ row }) => <span>{row.original.referenceMapping?.business_risk_context?.sensitive_data ?? "—"}</span>,
              },
            ]}
            data={risk}
            getRowId={(r) => String(r.id)}
            emptyMessage="No findings yet."
            enableColumnVisibility={false}
            enableColumnResizing
            enableColumnFilters
            maxBodyHeight="65vh"
          />
        </div>
      )}

      {tab === "acceptance" && (
        <div className="space-y-2">
          <p className="text-xs text-gray-400">Risk Acceptance requests — approval by Platform Admin.</p>
          <DataTable
            columns={[
              {
                id: "finding_title",
                accessorFn: (r) => r.finding_title,
                header: "Finding",
                enableSorting: false,
                meta: { filter: { variant: "text", label: "Finding", placeholder: "Search finding" } },
                cell: ({ row }) => <span>{row.original.finding_title || "—"}</span>,
              },
              {
                id: "finding_severity",
                accessorFn: (r) => r.finding_severity,
                header: "Severity",
                enableSorting: false,
                meta: {
                  filter: {
                    variant: "select",
                    label: "Severity",
                    placeholder: "All severities",
                    options: VAPT_SEVERITIES.map((v) => ({ value: v, label: v })),
                  },
                },
                cell: ({ row }) => <VaptSeverityBadge severity={row.original.finding_severity} size="sm" />,
              },
              {
                id: "requested_by",
                accessorFn: (r) => r.requested_by,
                header: "Requested By",
                enableSorting: false,
                meta: { filter: { variant: "text", label: "Requested By", placeholder: "Search requester" } },
                cell: ({ row }) => <span>{row.original.requested_by}</span>,
              },
              {
                id: "reason",
                accessorFn: (r) => r.reason,
                header: "Reason",
                enableSorting: false,
                cell: ({ row }) => <span>{row.original.reason}</span>,
              },
              {
                id: "status",
                accessorFn: (r) => r.status,
                header: "Status",
                enableSorting: false,
                meta: {
                  filter: {
                    variant: "select",
                    label: "Status",
                    placeholder: "All statuses",
                    options: [
                      { value: "Pending", label: "Pending" },
                      { value: "Approved", label: "Approved" },
                      { value: "Rejected", label: "Rejected" },
                    ],
                  },
                },
                cell: ({ row }) => <VaptStatusBadge status={row.original.status} size="sm" />,
              },
              {
                id: "expiry_date",
                accessorFn: (r) => r.expiry_date,
                header: "Expiry Date",
                enableSorting: false,
                cell: ({ row }) => <span>{row.original.expiry_date ? new Date(row.original.expiry_date).toLocaleDateString() : "—"}</span>,
              },
              {
                id: "actions",
                header: "Actions",
                enableSorting: false,
                cell: ({ row }) => {
                  const r = row.original;
                  return isPlatformAdmin && r.status === "Pending" ? (
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => decide(r.acceptance_id, "Approved")} className="text-xs font-semibold text-green-700 hover:text-green-800 underline">
                        Approve
                      </button>
                      <button type="button" onClick={() => decide(r.acceptance_id, "Rejected")} className="text-xs font-semibold text-red-600 hover:text-red-700 underline">
                        Reject
                      </button>
                    </div>
                  ) : (
                    "—"
                  );
                },
              },
            ]}
            data={acceptance}
            getRowId={(a) => String(a.acceptance_id)}
            emptyMessage="No risk acceptance requests recorded."
            enableColumnVisibility={false}
            enableColumnResizing
            enableColumnFilters
            maxBodyHeight="65vh"
          />
        </div>
      )}

      <FormModal open={showRequestModal} onClose={() => setShowRequestModal(false)} maxWidth="md">
        <FormModalHeader
          title="Request Risk Acceptance"
          subtitle="Requires Platform Admin approval. An accepted risk stays counted in the Risk score — it records the decision, not a fix."
          onClose={() => setShowRequestModal(false)}
        />
        <FormModalBody id="vapt-risk-acceptance-form" onSubmit={submitRequest}>
          <FormModalField label="Finding" required>
            <select
              className={formInputCls(false)}
              value={requestForm.finding}
              onChange={(e) => setRequestForm({ ...requestForm, finding: e.target.value })}
              required
            >
              {risk.map((r) => (
                <option key={r.id} value={r.mongo_id}>
                  {r.title} ({r.severity})
                </option>
              ))}
            </select>
          </FormModalField>
          <FormModalField label="Reason" required>
            <textarea
              className={formInputCls(false)}
              rows={3}
              value={requestForm.reason}
              onChange={(e) => setRequestForm({ ...requestForm, reason: e.target.value })}
              required
            />
          </FormModalField>
        </FormModalBody>
        <FormModalFooter formId="vapt-risk-acceptance-form" onCancel={() => setShowRequestModal(false)} saving={savingRequest} submitLabel="Request" />
      </FormModal>
    </div>
  );
};

export default VaptRisk;
